import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  CircleDot,
  Clock3,
  FileText,
  Gift,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { formatPts, pick, unwrapData, unwrapList } from '../../utils/moduleHelpers';
import '../../styles/module-shared.css';
import './rewards-module.css';

const FILTERS = [
  { id: 'All', api: '' },
  { id: 'Pending', api: 'Pending' },
  { id: 'Redeemed', api: 'Redeemed' },
];

const formatHistoryDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

const inferCategory = (item) => {
  const raw = String(
    item.category || item.rewardCategory || item.type || item.rewardType || ''
  ).trim();
  if (raw) return raw.toUpperCase();
  const name = String(item.rewardName || item.name || '').toLowerCase();
  if (name.includes('cash')) return 'CASH';
  if (name.includes('iphone') || name.includes('gift')) return 'GIFT';
  return 'REWARD';
};

const RewardsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('All');
  const [payload, setPayload] = useState({});

  const fetchData = useCallback(
    async ({ cancelled }) => {
      setLoading(true);
      setError('');
      try {
        const apiStatus = FILTERS.find((f) => f.id === status)?.api || '';
        const res = await apiService.getRewardsReport(apiStatus);
        if (cancelled()) return;
        if (!res.success) {
          const msg = res.error || 'Failed to load report';
          if (!String(msg).includes('404')) setError(msg);
        }
        setPayload(unwrapData(res) || {});
      } catch (err) {
        if (!cancelled()) {
          const msg = err.message || 'Failed to load report';
          if (!String(msg).includes('404')) setError(msg);
          setPayload({});
        }
      } finally {
        if (!cancelled()) setLoading(false);
      }
    },
    [status]
  );

  useFetchOnceByKey(`rewards-report|${status}`, fetchData, true);

  const items = useMemo(() => {
    const list = unwrapList({ data: payload });
    if (list.length) return list;
    return unwrapList({ data: payload.history }) || unwrapList({ data: payload.rewards }) || [];
  }, [payload]);

  const pointsToSpend = Number(
    pick(payload, 'pointsToSpend', 'availablePoints', 'totalLots', 'PointsToSpend') ?? 0
  );
  const historyCount = Number(
    pick(payload, 'historyCount', 'totalRecords', 'TotalRecords') ?? items.length
  );
  const pendingCount = Number(pick(payload, 'pendingCount', 'PendingCount') ?? 0);
  const claimedCount = Number(pick(payload, 'claimedCount', 'ClaimedCount') ?? 0);
  const myRewards = historyCount || pendingCount + claimedCount;

  const summaryCards = [
    { id: 'my', label: 'My Rewards', value: formatPts(myRewards), icon: Gift, tone: 'teal' },
    {
      id: 'redeemed',
      label: 'Redeemed',
      value: formatPts(claimedCount),
      icon: CheckCircle2,
      tone: 'green',
    },
    { id: 'pending', label: 'Pending', value: formatPts(pendingCount), icon: Clock3, tone: 'amber' },
    {
      id: 'points',
      label: 'Points to Spend',
      value: formatPts(pointsToSpend),
      icon: CircleDot,
      tone: 'sky',
      emphasize: true,
    },
  ];

  return (
    <div className="page rd-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={FileText}
          title="Rewards Report"
          subtitle="Rewards you have claimed, redeemed, or have pending."
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />
        <ModuleAlert message={error} />

        <section className="rr-hero">
          <div className="rr-hero__copy">
            <span className="rr-hero__eyebrow">Activity report</span>
            <h2>Track every reward you claimed</h2>
            <p>
              See pending claims, completed redemptions, and how many points you still have ready to
              spend.
            </p>
          </div>
          <div className="rr-hero__aside">
            <div className="rr-hero__points">
              <strong>{formatPts(pointsToSpend)}</strong>
              <span>Points to spend</span>
            </div>
            <Link className="rr-hero__cta" to="/rewards/claim">
              Browse Rewards <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <section className="rr-summary">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.id} className={`rr-summary-card is-${card.tone}`}>
                <div className="rr-summary-card__icon">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="rr-summary-card__label">{card.label}</span>
                <strong className={`rr-summary-card__value${card.emphasize ? ' is-accent' : ''}`}>
                  {card.value}
                </strong>
              </article>
            );
          })}
        </section>

        <section className="rr-history">
          <div className="rr-history__head">
            <div className="rr-history__title">
              <h3>Reward history</h3>
              <span className="rr-history__count">{loading ? '…' : items.length}</span>
            </div>
            <div className="rr-history__filters" role="tablist" aria-label="History filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={status === f.id}
                  className={`rr-filter${status === f.id ? ' is-active' : ''}`}
                  onClick={() => setStatus(f.id)}
                >
                  {f.id}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="rr-history__loading">
              <AppLoader color="#8b0000" />
            </div>
          ) : items.length === 0 ? (
            <div className="rr-history__empty">
              <Gift className="w-10 h-10 mx-auto mb-3 text-red-300" />
              <h4>No reward history yet</h4>
              <p>When you claim rewards, they will appear here with status updates.</p>
              <Link className="rr-hero__cta rr-hero__cta--inline" to="/rewards/claim">
                Browse Rewards <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="rr-history__list">
              {items.map((item, index) => {
                const redeemed =
                  item.isClaimed === true ||
                  String(item.status || item.rewardStatus || '')
                    .toLowerCase()
                    .includes('redeem');
                const pointsSpent = Number(
                  item.pointsCost ??
                    item.pointsDeducted ??
                    item.rewardRequiredPoints ??
                    item.requiredPoints ??
                    item.pointsUsed ??
                    item.points ??
                    item.requiredLots ??
                    0
                );
                const created =
                  item.createdDateTime ||
                  item.CreatedDateTime ||
                  item.createdAt ||
                  item.claimedDate ||
                  item.date ||
                  item.claimDate ||
                  '';
                const category = inferCategory(item);

                return (
                  <article
                    key={item.id || item.affiliateRewardId || index}
                    className="rr-card"
                  >
                    <div className="rr-card__top">
                      <div className="rr-card__spent">
                        <span className="rr-card__gift">
                          <Gift className="w-3.5 h-3.5" />
                        </span>
                        <div>
                          <span className="rr-card__spent-label">Points spent</span>
                          <strong>{formatPts(pointsSpent)} pts</strong>
                        </div>
                      </div>
                      <span className={`rr-card__badge${redeemed ? ' is-redeemed' : ' is-pending'}`}>
                        <i />
                        {redeemed ? 'Redeemed' : 'Pending'}
                      </span>
                    </div>

                    <div className="rr-card__body">
                      <span className="rr-card__tag">{category}</span>
                      <h4>{item.rewardName || item.name || `Reward #${index + 1}`}</h4>
                      <div className={`rr-card__footer${redeemed ? ' is-redeemed' : ''}`}>
                        {redeemed ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <Clock3 className="w-3.5 h-3.5" />
                        )}
                        <span>
                          {redeemed ? 'Redeemed' : 'Created'} {formatHistoryDate(created)}
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RewardsReport;
